/**
 * Google reCAPTCHA v3 Utility
 * 
 * This utility handles reCAPTCHA v3 token generation.
 * reCAPTCHA v3 is invisible and runs in the background.
 */

/**
 * Loads reCAPTCHA script dynamically if not already loaded
 * @returns {Promise<void>}
 */
const loadRecaptchaScript = () => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        resolve();
      });
      return;
    }

    // Check if script tag already exists
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      reject(new Error('reCAPTCHA site key not configured'));
      return;
    }

    const existingScript = document.querySelector(
      `script[src*="recaptcha/api.js"]`
    );

    if (existingScript) {
      // Script is loading, wait for it
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            resolve();
          });
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    // Load script dynamically
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          resolve();
        });
      } else {
        resolve(); // Resolve anyway, grecaptcha should be available
      }
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load reCAPTCHA script'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Waits for the reCAPTCHA script to load
 * @returns {Promise<void>}
 */
const waitForRecaptcha = () => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(() => {
        resolve();
      });
    } else {
      // Try loading script first
      loadRecaptchaScript()
        .then(() => resolve())
        .catch((error) => {
          // If loading fails, poll for existing script
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait
          const interval = setInterval(() => {
            attempts++;
            if (window.grecaptcha && window.grecaptcha.ready) {
              window.grecaptcha.ready(() => {
                clearInterval(interval);
                resolve();
              });
            } else if (attempts >= maxAttempts) {
              clearInterval(interval);
              reject(error || new Error('reCAPTCHA script failed to load'));
            }
          }, 100);
        });
    }
  });
};

/**
 * Gets a reCAPTCHA v3 token
 * @param {string} action - The action name (e.g., 'signup', 'login')
 * @returns {Promise<string>} The reCAPTCHA token
 */
export const getRecaptchaToken = async (action = 'signup') => {
  try {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (!siteKey) {
      console.warn('reCAPTCHA site key not configured. Skipping reCAPTCHA validation.');
      return null;
    }

    // Wait for reCAPTCHA to be ready
    await waitForRecaptcha();

    // Execute reCAPTCHA and get token
    const token = await window.grecaptcha.execute(siteKey, { action });

    return token;
  } catch (error) {
    console.error('Error getting reCAPTCHA token:', error);
    // Return null to allow signup to proceed if reCAPTCHA fails
    // Backend should handle validation and decide whether to accept or reject
    return null;
  }
};

/**
 * Checks if reCAPTCHA is available
 * @returns {boolean}
 */
export const isRecaptchaAvailable = () => {
  return !!(window.grecaptcha && process.env.REACT_APP_RECAPTCHA_SITE_KEY);
};

